import { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Calendar, MapPin, Info, Home, Plus, Bookmark, Sun, Moon, Gift, HelpCircle, BookOpen, Wrench, Download, Smartphone, Globe, Baby, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useTheme } from "@/context/ThemeContext";
import { useLanguage } from "@/context/LanguageContext";
import SubmitEventModal from "@/components/SubmitEventModal";
import AddToHomescreen from "@/components/AddToHomescreen";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [bookmarkCount, setBookmarkCount] = useState(0);
  const [isStandalone, setIsStandalone] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { language, changeLanguage, t, languageNames } = useLanguage();
  const pwaRef = useRef(null);
  const clickTimeoutRef = useRef(null);

  // Navigation links with translations
  const navLinks = [
    { href: "/", label: t("home"), icon: Home },
    { href: "/events", label: t("events"), icon: MapPin },
    { href: "/free-events", label: t("free"), icon: Gift },
    { href: "/family-events", label: "Family", icon: Baby },
    { href: "/calendar", label: t("calendar"), icon: Calendar },
    { href: "/blog", label: "Blog", icon: BookOpen },
    { href: "/tools", label: t("tools"), icon: Wrench },
    { href: "/referral", label: "Referral", icon: Users },
    { href: "/bookmarks", label: t("saved"), icon: Bookmark }
  ];

  // Secret admin access - click logo 5 times
  const handleLogoClick = (e) => {
    e.preventDefault();
    
    // Clear previous timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);
    
    // Reset after 2 seconds of no clicks
    clickTimeoutRef.current = setTimeout(() => {
      setLogoClickCount(0);
    }, 2000);
    
    // Navigate to admin after 5 clicks
    if (newCount >= 5) {
      setLogoClickCount(0);
      navigate('/admin');
    }
  };

  // Use React Router navigation for SPA experience
  const handleNavClick = (e, href) => {
    e.preventDefault();
    setMobileMenuOpen(false);
    navigate(href);
  };

  useEffect(() => {
    // Check if already installed as PWA
    const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;
    setIsStandalone(standalone);
  }, []);

  useEffect(() => {
    const updateCount = () => {
      const bookmarks = JSON.parse(localStorage.getItem("upnext_bookmarks") || "[]");
      setBookmarkCount(bookmarks.length);
    };
    updateCount();
    window.addEventListener("storage", updateCount);
    const interval = setInterval(updateCount, 1000);
    return () => {
      window.removeEventListener("storage", updateCount);
      clearInterval(interval);
    };
  }, []);

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50">
        <div className="glass-card mx-4 md:mx-8 mt-4 rounded-2xl">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Logo - Secret admin access (click 5x) */}
              <div 
                onClick={handleLogoClick}
                className="flex items-center gap-1 cursor-pointer select-none"
                data-testid="logo-link"
                title={logoClickCount > 0 ? `${5 - logoClickCount} more...` : undefined}
              >
                <div className="flex items-center gap-1">
                  <span className="text-2xl font-extrabold bg-gradient-to-r from-pink-500 to-rose-500 bg-clip-text text-transparent">UP</span>
                  <span className="text-2xl font-extrabold text-foreground">NEXT</span>
                  <span className="text-lg font-semibold text-muted-foreground ml-1">CALENDAR</span>
                </div>
                <div className="hidden sm:block ml-2 pl-2 border-l border-white/20">
                  <div className="text-xs text-muted-foreground leading-tight">
                    Malaysia 2026
                  </div>
                </div>
              </div>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-1">
                {navLinks.map(link => (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={(e) => handleNavClick(e, link.href)}
                    data-testid={`nav-${link.label.toLowerCase()}`}
                    className={cn(
                      "px-4 py-2 rounded-xl text-sm font-medium transition-all relative cursor-pointer",
                      location.pathname === link.href
                        ? "bg-gradient-to-r from-pink-500/20 to-violet-500/20 text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                    )}
                  >
                    {link.label}
                    {link.href === "/bookmarks" && bookmarkCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-pink-500 text-white text-xs flex items-center justify-center font-bold">
                        {bookmarkCount}
                      </span>
                    )}
                  </a>
                ))}
              </nav>

              {/* CTA Buttons */}
              <div className="hidden md:flex items-center gap-2">
                {/* Language Selector */}
                <div className="relative">
                  <button
                    onClick={() => setLangMenuOpen(!langMenuOpen)}
                    className="p-2 rounded-xl hover:bg-white/10 dark:hover:bg-white/5 transition-colors flex items-center gap-1"
                    data-testid="language-toggle"
                  >
                    <Globe className="h-5 w-5 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground uppercase">{language}</span>
                  </button>
                  {langMenuOpen && (
                    <div className="absolute right-0 mt-2 w-40 glass-card rounded-xl border shadow-lg py-1 z-50">
                      {Object.entries(languageNames).map(([code, name]) => (
                        <button
                          key={code}
                          onClick={() => {
                            changeLanguage(code);
                            setLangMenuOpen(false);
                          }}
                          className={cn(
                            "w-full px-4 py-2 text-left text-sm hover:bg-white/10 transition-colors",
                            language === code ? "text-pink-400 font-medium" : "text-foreground"
                          )}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Theme Toggle */}
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-xl hover:bg-white/10 dark:hover:bg-white/5 transition-colors"
                  data-testid="theme-toggle"
                  aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                >
                  {theme === "dark" ? (
                    <Sun className="h-5 w-5 text-amber-400" />
                  ) : (
                    <Moon className="h-5 w-5 text-slate-600" />
                  )}
                </button>

                {/* Install App Button - Hide if already installed */}
                {!isStandalone && (
                  <Button
                    onClick={() => pwaRef.current?.openPrompt()}
                    variant="outline"
                    className="border-violet-500/50 text-violet-400 hover:bg-violet-500/10 rounded-xl px-4 h-9 text-sm font-semibold"
                    data-testid="install-app-btn"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    {t("installApp")}
                  </Button>
                )}
                
                <Button
                  onClick={() => setSubmitModalOpen(true)}
                  variant="outline"
                  className="border-teal-500/50 text-teal-400 hover:bg-teal-500/10 rounded-xl px-4 h-9 text-sm font-semibold"
                  data-testid="submit-event-header-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  {t("submitEvent")}
                </Button>
                <Button
                  asChild
                  className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white rounded-xl px-5 h-9 text-sm font-semibold"
                  data-testid="header-cta"
                >
                  <Link to="/events">
                    {t("explore")}
                  </Link>
                </Button>
              </div>

              {/* Mobile Menu Button */}
              <div className="flex md:hidden items-center gap-2">
                {/* Language Toggle Mobile */}
                <button
                  onClick={() => {
                    const langs = ['bm', 'en', 'zh', 'ta'];
                    const currentIdx = langs.indexOf(language);
                    const nextLang = langs[(currentIdx + 1) % langs.length];
                    changeLanguage(nextLang);
                  }}
                  className="p-2 rounded-xl hover:bg-white/10 dark:hover:bg-white/5 transition-colors"
                  data-testid="language-toggle-mobile"
                >
                  <Globe className="h-5 w-5 text-muted-foreground" />
                </button>

                {/* Theme Toggle Mobile */}
                <button
                  onClick={toggleTheme}
                  className="p-2 rounded-xl hover:bg-white/10 dark:hover:bg-white/5 transition-colors"
                  data-testid="theme-toggle-mobile"
                  aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                >
                  {theme === "dark" ? (
                    <Sun className="h-5 w-5 text-amber-400" />
                  ) : (
                    <Moon className="h-5 w-5 text-slate-600" />
                  )}
                </button>
                
                <button
                  className="p-2 rounded-xl hover:bg-white/5 text-foreground"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  data-testid="mobile-menu-btn"
                >
                  {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Mobile Navigation */}
            {mobileMenuOpen && (
              <nav className="md:hidden pt-4 pb-2 border-t border-white/10 mt-4">
                <div className="flex flex-col gap-1">
                  {navLinks.map(link => (
                    <a
                      key={link.href}
                      href={link.href}
                      onClick={(e) => handleNavClick(e, link.href)}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      className={cn(
                        "flex items-center justify-between px-4 py-3 rounded-xl text-sm font-medium transition-all cursor-pointer",
                        location.pathname === link.href
                          ? "bg-gradient-to-r from-pink-500/20 to-violet-500/20 text-foreground"
                          : "text-muted-foreground hover:text-foreground hover:bg-white/5"
                      )}
                    >
                      <span className="flex items-center gap-3">
                        <link.icon className="h-4 w-4" />
                        {link.label}
                      </span>
                      {link.label === "Saved" && bookmarkCount > 0 && (
                        <span className="w-5 h-5 rounded-full bg-pink-500 text-white text-xs flex items-center justify-center font-bold">
                          {bookmarkCount}
                        </span>
                      )}
                    </a>
                  ))}
                  {/* Install App Button for Mobile - Hide if already installed */}
                  {!isStandalone && (
                    <Button
                      onClick={() => {
                        setMobileMenuOpen(false);
                        pwaRef.current?.openPrompt();
                      }}
                      variant="outline"
                      className="border-violet-500/50 text-violet-400 rounded-xl mt-2"
                      data-testid="mobile-install-app-btn"
                    >
                      <Smartphone className="h-4 w-4 mr-2" />
                      Install App
                    </Button>
                  )}
                  <Button
                    onClick={() => {
                      setMobileMenuOpen(false);
                      setSubmitModalOpen(true);
                    }}
                    variant="outline"
                    className="border-teal-500/50 text-teal-400 rounded-xl mt-2"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Submit Event
                  </Button>
                  <Button
                    asChild
                    className="bg-gradient-to-r from-pink-500 to-rose-500 text-white rounded-xl mt-2"
                  >
                    <Link to="/events" onClick={() => setMobileMenuOpen(false)}>
                      Explore Events
                    </Link>
                  </Button>
                </div>
              </nav>
            )}
          </div>
        </div>
      </header>

      <SubmitEventModal 
        open={submitModalOpen} 
        onClose={() => setSubmitModalOpen(false)} 
      />

      {/* PWA Install Prompt - controlled from Header */}
      <AddToHomescreen ref={pwaRef} showOnLoad={false} />
    </>
  );
}
